import classNames from 'classnames'
import React, { useEffect, useRef } from 'react'
import '../styles/stickySubMenu.css'
import { isNullOrWhiteSpace } from '../utilities/string'
import { ScrollProgressBar } from './Header'

export enum MenuVariant {
  PortfolioPage,
}

export interface StickySubMenuItem {
  active?: boolean
  link: string
  title: string
}

export interface StickySubMenuProps {
  additionalContentClassName?: string
  additionalItemClassName?: string
  additionalStickyWrapperClassName?: string
  backgroundWidthClassName?: string
  borderColorClass?: string
  gaTrackingId?: string
  id?: string
  items: StickySubMenuItem[]
  justifyListClassName?:
    | 'justify-between tb:justify-center'
    | 'justify-between tb:justify-around'
    | 'justify-start'
  offset?: number
  progressBarEnabled?: boolean
  scrollOffset?: number
  textClassName?: string
}

export const StickySubMenu = React.forwardRef<HTMLDivElement, StickySubMenuProps>(
  (
    {
      additionalContentClassName = '',
      additionalItemClassName = '',
      additionalStickyWrapperClassName = '',
      backgroundWidthClassName = 'w-full',
      borderColorClass = 'border-neutral-lighter',
      gaTrackingId,
      id,
      items,
      justifyListClassName = 'justify-between tb:justify-center',
      offset = 0,
      progressBarEnabled,
      textClassName = classNames(
        'font-t text-t-300',
        'sm:text-t-400',
        'md:text-t-500',
        'xl:text-t-600'
      ),
    },
    ref
  ) => {
    const backgroundRef = useRef<HTMLSpanElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const menuRef = useRef<HTMLUListElement>(null)

    const isWideScrolling = justifyListClassName === 'justify-start'
    const autoScrollTimer = useRef<NodeJS.Timeout>()

    useEffect(() => {
      const scrollContainer = scrollRef.current
      const menuList = menuRef.current
      if (
        menuList &&
        scrollContainer &&
        scrollContainer.scrollWidth > scrollContainer.clientWidth
      ) {
        const activeIndex = items.findIndex(item => item.active)
        const child = menuList.children[activeIndex]
        if (child) {
          const offset = 30
          const parentRect = scrollContainer.getBoundingClientRect()
          const childRect = child.getBoundingClientRect()
          const leftDiff = childRect.left - offset - parentRect.left
          const rightDiff = childRect.right + offset - parentRect.right
          if (leftDiff < 0 || rightDiff > 0) {
            const scrollBy = leftDiff < 0 ? leftDiff : rightDiff
            // stagger scrolling to avoid jerkiness
            if (autoScrollTimer.current) {
              clearTimeout(autoScrollTimer.current)
            }

            autoScrollTimer.current = setTimeout(
              () =>
                scrollContainer.scrollBy({
                  left: scrollBy,
                  behavior: 'smooth',
                }),
              100
            )
          }
        }
      }

      return () => {
        if (autoScrollTimer.current) {
          clearTimeout(autoScrollTimer.current)
        }
      }
    }, [items])

    return (
      <div
        id={id}
        className={`
          sticky
          ${additionalStickyWrapperClassName}
        `}
        ref={ref}
        style={{
          top: offset,
        }}
      >
        <span
          className={classNames(
            'relative flex items-center justify-center transition-all duration-200',
            additionalContentClassName
          )}
        >
          {/*
            Background - hack to ensure background color can span full screen width without modifying
            any other layout which often needs to remain intact
          */}
          <span
            className={classNames(
              'bg-site-background absolute top-0 left-1/2 h-full border-t border-b transition-all duration-200 transform -translate-x-1/2',
              borderColorClass,
              backgroundWidthClassName
            )}
            ref={backgroundRef}
          >
            {progressBarEnabled && <ScrollProgressBar parentRef={backgroundRef} />}
          </span>

          <div
            className={classNames(
              'flex h-full overflow-x-auto overflow-y-hidden hide-scrollbar',
              { 'min-w-full': !isWideScrolling }
            )}
            ref={scrollRef}
          >
            <ul
              className={classNames(
                'relative flex flex-row h-full items-center',
                isWideScrolling
                  ? 'gap-30 tb:gap-45 md:gap-60 whitespace-nowrap'
                  : 'min-w-full gap-15 tb:gap-30 sm:gap-45 md:gap-30 xl:gap-60 xxl:gap-45',
                justifyListClassName
              )}
              ref={menuRef}
            >
              {items.map((item, index) => {
                if (!item.link || !item.title) {
                  return null
                }

                return (
                  <li
                    key={index}
                    className={classNames(
                      'flex-none',
                      'tb:flex-shrink',
                      {
                        'first:ml-30 last:mr-30': isWideScrolling,
                      },
                      additionalItemClassName,
                      textClassName
                    )}
                  >
                    <a
                      className="block group py-5 sm:py-10"
                      data-ga-tracking-id={gaTrackingId}
                      data-ga-tracking-name={
                        !isNullOrWhiteSpace(gaTrackingId) ? item.title : undefined
                      }
                      href={item.link}
                    >
                      <span
                        className={classNames(
                          'text-primary border-b duration-100 group-hover:duration-250 group-hover:border-primary',
                          {
                            'border-primary': item.active,
                            'border-transparent': !item.active,
                          }
                        )}
                      >
                        {item.title}
                      </span>
                    </a>
                  </li>
                )
              })}
            </ul>
          </div>
        </span>
      </div>
    )
  }
)
